/**
 * useSignature Hook
 *
 * Manages signature mode (typed / drawn), signature data,
 * and the `hasSignature` derived state.
 *
 * Extracted from ReviewSign.
 */

import { useState, useCallback, useMemo } from 'react';

export type SignatureMode = 'typed' | 'drawn';

export interface UseSignatureOptions {
  /** Initial typed signature value */
  initialTyped?: string;
  /** Initial drawn signature data URL */
  initialDrawn?: string;
  /** Initial mode */
  initialMode?: SignatureMode;
}

export interface UseSignatureReturn {
  /** Current signature mode */
  signatureMode: SignatureMode;
  /** Switch signature mode */
  setSignatureMode: (mode: SignatureMode) => void;
  /** Typed signature text */
  typedSignature: string;
  /** Update typed signature */
  setTypedSignature: (value: string) => void;
  /** Drawn signature data URL */
  drawnSignatureUrl: string;
  /** Update drawn signature data URL */
  setDrawnSignatureUrl: (url: string) => void;
  /** Whether the active mode has a valid signature */
  hasSignature: boolean;
  /** Get the signature data for the currently active mode */
  getActiveSignatureData: () => { typed: string; drawn: string };
}

export function useSignature(options: UseSignatureOptions = {}): UseSignatureReturn {
  const [signatureMode, setSignatureMode] = useState<SignatureMode>(options.initialMode || 'typed');
  const [typedSignature, setTypedSignature] = useState(options.initialTyped || '');
  const [drawnSignatureUrl, setDrawnSignatureUrl] = useState(options.initialDrawn || '');

  const hasSignature = useMemo(() => {
    return signatureMode === 'typed'
      ? typedSignature.trim().length >= 2
      : drawnSignatureUrl.length > 0;
  }, [signatureMode, typedSignature, drawnSignatureUrl]);

  const getActiveSignatureData = useCallback(() => ({
    typed: signatureMode === 'typed' ? typedSignature : '',
    drawn: signatureMode === 'drawn' ? drawnSignatureUrl : '',
  }), [signatureMode, typedSignature, drawnSignatureUrl]);

  return {
    signatureMode,
    setSignatureMode,
    typedSignature,
    setTypedSignature,
    drawnSignatureUrl,
    setDrawnSignatureUrl,
    hasSignature,
    getActiveSignatureData,
  };
}
